import React, { useContext, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import axios from "axios";
import { server } from "../../common";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Radio,
  FormControlLabel,
  RadioGroup,
  Checkbox,
  Button,
  Card,
  CardHeader,
  CardContent,
  Grid,
  FormGroup,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import configContext from "../../contexts/configContext/configContexts";
import Loader from "../../components/Loader";
import { useTranslation } from "react-i18next";

const swalalert = withReactContent(Swal);

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  borderRadius: 8,
}));

const StyledMainCardHeader = styled(CardHeader)(({ theme }) => ({
  backgroundColor: "#2E3B5E",
  color: theme.palette.common.white,
  fontWeight: 600,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.5, 4),
  fontSize: "1.1rem",
  fontWeight: 500,
  borderRadius: 8,
  textTransform: "none",
  "&:disabled": {
    backgroundColor: theme.palette.grey,
    color: theme.palette.grey,
  },
}));

const initialValues = {
  full_name: "",
  father_husband_name: "",
  address: "",
  contact_number: "",
  email_id: "",
  stall_type: "",
  products_services: "",
  stall_size: "",
  custom_size: "",
  number_of_stalls: "",
  division_area: "",
  street_ground: "",
  event_name: "",
  event_start_date: "",
  event_end_date: "",
  duration_required: "",
  stall_fee: "",
  payment_mode: "",
  identity_proof: null,
  address_proof: null,
  passport_photo: null,
  gst_license: null,
};

const StallApplicationForm = () => {
  const [isChecked, setIsChecked] = useState(false);
  const [processing, setProcessing] = useState(false);
  const { setProgress } = useContext(configContext);
  const { t } = useTranslation();

  const handleCheckboxChange = (e) => {
    setIsChecked(e.target.checked);
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: Yup.object({
      full_name: Yup.string().required("Enter Full Name"),
      father_husband_name: Yup.string().required("Enter Father's/Husband's Name"),
      address: Yup.string().required("Enter Address"),
      contact_number: Yup.string()
        .matches(/^[6-9]\d{9}$/, "Please enter a valid Indian mobile number")
        .required("Enter Contact Number"),
      email_id: Yup.string().email("Invalid email").required("Enter Email ID"),
      stall_type: Yup.string().required("Enter Type of Stall/Business"),
      products_services: Yup.string().required("Enter Products/Services"),
      stall_size: Yup.string().required("Select Stall Size"),
      number_of_stalls: Yup.number()
        .positive("Number must be positive")
        .required("Enter Number of Stalls"),
      division_area: Yup.string().required("Enter Division/Area"),
      street_ground: Yup.string().required("Enter Street/Ground"),
      event_name: Yup.string().required("Enter Event Name"),
      event_start_date: Yup.string().required("Select Start Date"),
      event_end_date: Yup.string().required("Select End Date"),
      duration_required: Yup.string().required("Enter Duration Required"),
      stall_fee: Yup.number().positive("Fee must be positive").required("Enter Stall Fee"),
      payment_mode: Yup.string().required("Select Payment Mode"),
      identity_proof: Yup.mixed()
        .required("Please upload Identity Proof")
        .test("fileSize", "File size is too large", (value) => {
          return value ? value.size <= 5000000 : true;
        }),
      address_proof: Yup.mixed()
        .required("Please upload Address Proof")
        .test("fileSize", "File size is too large", (value) => {
          return value ? value.size <= 5000000 : true;
        }),
      passport_photo: Yup.mixed()
        .required("Please upload Passport Photo")
        .test("fileSize", "File size is too large", (value) => {
          return value ? value.size <= 5000000 : true;
        }),
    }),
    onSubmit: (values, action) => {
      const formData = new FormData();
      
      // Append all form values
      Object.keys(values).forEach(key => {
        if (values[key] !== null && values[key] !== "") {
          formData.append(key, values[key]);
        }
      });

      swalalert
        .fire({
          title: t("stallform.submit_confirmation_title"),
          text: t("stallform.submit_confirmation_text"),
          type: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: t("stallform.yes"),
          cancelButtonText: t("stallform.no"),
          buttonsStyling: true,
        })
        .then((swalObject) => {
          if (swalObject.isConfirmed) {
            setProcessing(true);
            setProgress(10);
            server
              .post(`/stall/stallapplication`, formData, {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              })
              .then(function (response) {
                if (response.status === 200 || response.status === 201) {
                  swalalert.fire({
                    title: t("stallform.success_title"),
                    text: `${t("stallform.success_message")} ${response.data.id}`,
                    icon: "success",
                  });
                  setProgress(100);
                  formik.resetForm();
                }
                setProcessing(false);
              })
              .catch((error) => {
                toast.error(
                  error.response?.data?.message ||
                    error.response?.data?.error ||
                    t("stallform.server_error")
                );
                setProcessing(false);
                setProgress(100);
              });
          }
        });
    },
  });

  return (
    <div className="container mt-4">
      {processing && <Loader loading={processing} />}

      <StyledCard>
        <h4
          style={{
            backgroundColor: "#81BFDA",
            padding: "10px",
            color: "black",
          }}
        >
          {t("stallform.title")}
        </h4>
        <CardContent>
          <form onSubmit={formik.handleSubmit}>
            {/* Applicant Details */}
            <StyledCard>
              <h6 style={{ backgroundColor: "#D1E9F6", padding: "10px" }}>
                {t("stallform.applicant_details")}
              </h6>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("stallform.full_name")}
                      name="full_name"
                      value={formik.values.full_name}
                      onChange={formik.handleChange}
                      error={formik.touched.full_name && Boolean(formik.errors.full_name)}
                      helperText={formik.touched.full_name && formik.errors.full_name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("stallform.father_husband_name")}
                      name="father_husband_name"
                      value={formik.values.father_husband_name}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.father_husband_name && Boolean(formik.errors.father_husband_name)
                      }
                      helperText={
                        formik.touched.father_husband_name && formik.errors.father_husband_name
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label={t("stallform.address")}
                      name="address"
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      error={formik.touched.address && Boolean(formik.errors.address)}
                      helperText={formik.touched.address && formik.errors.address}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("stallform.contact_number")}
                      name="contact_number"
                      value={formik.values.contact_number}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.contact_number && Boolean(formik.errors.contact_number)
                      }
                      helperText={formik.touched.contact_number && formik.errors.contact_number}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("stallform.email_id")}
                      name="email_id"
                      type="email"
                      value={formik.values.email_id}
                      onChange={formik.handleChange}
                      error={formik.touched.email_id && Boolean(formik.errors.email_id)}
                      helperText={formik.touched.email_id && formik.errors.email_id}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>

            {/* Stall Information */}
            <StyledCard>
              <h6 style={{ backgroundColor: "#D1E9F6", padding: "10px" }}>
                {t("stallform.stall_information")}
              </h6>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("stallform.stall_type")}
                      name="stall_type"
                      value={formik.values.stall_type}
                      onChange={formik.handleChange}
                      error={formik.touched.stall_type && Boolean(formik.errors.stall_type)}
                      helperText={formik.touched.stall_type && formik.errors.stall_type}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("stallform.products_services")}
                      name="products_services"
                      value={formik.values.products_services}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.products_services && Boolean(formik.errors.products_services)
                      }
                      helperText={
                        formik.touched.products_services && formik.errors.products_services
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      error={formik.touched.stall_size && Boolean(formik.errors.stall_size)}
                    >
                      <InputLabel>{t("stallform.stall_size")}</InputLabel>
                      <Select
                        name="stall_size"
                        value={formik.values.stall_size}
                        onChange={formik.handleChange}
                        label={t("stallform.stall_size")}
                      >
                        <MenuItem value="">{t("stallform.select_size")}</MenuItem>
                        <MenuItem value="6x6">6x6 ft</MenuItem>
                        <MenuItem value="10x10">10x10 ft</MenuItem>
                        <MenuItem value="other">{t("stallform.other")}</MenuItem>
                      </Select>
                      {formik.touched.stall_size && formik.errors.stall_size && (
                        <Typography color="error">{formik.errors.stall_size}</Typography>
                      )}
                    </FormControl>
                  </Grid>
                  {formik.values.stall_size === "other" && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={t("stallform.custom_size")}
                        name="custom_size"
                        value={formik.values.custom_size}
                        onChange={formik.handleChange}
                        placeholder="Enter custom size"
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("stallform.number_of_stalls")}
                      name="number_of_stalls"
                      type="number"
                      value={formik.values.number_of_stalls}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.number_of_stalls && Boolean(formik.errors.number_of_stalls)
                      }
                      helperText={
                        formik.touched.number_of_stalls && formik.errors.number_of_stalls
                      }
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>

            {/* Location Preference */}
            <StyledCard>
              <h6 style={{ backgroundColor: "#D1E9F6", padding: "10px" }}>
                {t("stallform.location_preference")}
              </h6>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("stallform.division_area")}
                      name="division_area"
                      value={formik.values.division_area}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.division_area && Boolean(formik.errors.division_area)
                      }
                      helperText={formik.touched.division_area && formik.errors.division_area}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("stallform.street_ground")}
                      name="street_ground"
                      value={formik.values.street_ground}
                      onChange={formik.handleChange}
                      error={formik.touched.street_ground && Boolean(formik.errors.street_ground)}
                      helperText={formik.touched.street_ground && formik.errors.street_ground}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>

            {/* Event Details */}
            <StyledCard>
              <h6 style={{ backgroundColor: "#D1E9F6", padding: "10px" }}>
                {t("stallform.event_details")}
              </h6>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("stallform.event_name")}
                      name="event_name"
                      value={formik.values.event_name}
                      onChange={formik.handleChange}
                      error={formik.touched.event_name && Boolean(formik.errors.event_name)}
                      helperText={formik.touched.event_name && formik.errors.event_name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label={t("stallform.event_start_date")}
                      name="event_start_date"
                      type="date"
                      value={formik.values.event_start_date}
                      onChange={formik.handleChange}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: new Date().toISOString().split("T"),
                      }}
                      error={
                        formik.touched.event_start_date && Boolean(formik.errors.event_start_date)
                      }
                      helperText={formik.touched.event_start_date && formik.errors.event_start_date}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label={t("stallform.event_end_date")}
                      name="event_end_date"
                      type="date"
                      value={formik.values.event_end_date}
                      onChange={formik.handleChange}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: formik.values.event_start_date || new Date().toISOString().split("T"),
                      }}
                      error={formik.touched.event_end_date && Boolean(formik.errors.event_end_date)}
                      helperText={formik.touched.event_end_date && formik.errors.event_end_date}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <TextField
                      fullWidth
                      label={t("stallform.duration_required")}
                      name="duration_required"
                      value={formik.values.duration_required}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.duration_required && Boolean(formik.errors.duration_required)
                      }
                      helperText={
                        formik.touched.duration_required && formik.errors.duration_required
                      }
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>

            {/* Payment Details */}
            <StyledCard>
              <h6 style={{ backgroundColor: "#D1E9F6", padding: "10px" }}>
                {t("stallform.payment_details")}
              </h6>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("stallform.stall_fee")}
                      name="stall_fee"
                      type="number"
                      value={formik.values.stall_fee}
                      onChange={formik.handleChange}
                      InputProps={{
                        startAdornment: <Typography>₹</Typography>,
                      }}
                      error={formik.touched.stall_fee && Boolean(formik.errors.stall_fee)}
                      helperText={formik.touched.stall_fee && formik.errors.stall_fee}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      error={formik.touched.payment_mode && Boolean(formik.errors.payment_mode)}
                    >
                      <InputLabel>{t("stallform.payment_mode")}</InputLabel>
                      <Select
                        name="payment_mode"
                        value={formik.values.payment_mode}
                        onChange={formik.handleChange}
                        label={t("stallform.payment_mode")}
                      >
                        <MenuItem value="">{t("stallform.select_payment")}</MenuItem>
                        <MenuItem value="cash">{t("stallform.cash")}</MenuItem>
                        <MenuItem value="online">{t("stallform.online")}</MenuItem>
                        <MenuItem value="dd_cheque">{t("stallform.dd_cheque")}</MenuItem>
                      </Select>
                      {formik.touched.payment_mode && formik.errors.payment_mode && (
                        <Typography color="error">{formik.errors.payment_mode}</Typography>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>

            {/* Documents Upload */}
            <StyledCard>
              <h6 style={{ backgroundColor: "#D1E9F6", padding: "10px" }}>
                {t("stallform.documents_upload")}
              </h6>
              <CardContent>
                <Typography variant="subtitle2" color="error" gutterBottom>
                  {t("stallform.file_upload_note")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <label>
                      {t("stallform.identity_proof")}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="file"
                      name="identity_proof"
                      className="form-control"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(event) => {
                        formik.setFieldValue("identity_proof", event.currentTarget.files);
                      }}
                      style={{ marginBottom: "1rem" }}
                    />
                    {formik.touched.identity_proof && formik.errors.identity_proof && (
                      <Typography color="error">{formik.errors.identity_proof}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <label>
                      {t("stallform.address_proof")}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="file"
                      name="address_proof"
                      className="form-control"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(event) => {
                        formik.setFieldValue("address_proof", event.currentTarget.files);
                      }}
                      style={{ marginBottom: "1rem" }}
                    />
                    {formik.touched.address_proof && formik.errors.address_proof && (
                      <Typography color="error">{formik.errors.address_proof}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <label>
                      {t("stallform.passport_photo")}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="file"
                      name="passport_photo"
                      className="form-control"
                      accept=".jpg,.jpeg,.png"
                      onChange={(event) => {
                        formik.setFieldValue("passport_photo", event.currentTarget.files);
                      }}
                      style={{ marginBottom: "1rem" }}
                    />
                    {formik.touched.passport_photo && formik.errors.passport_photo && (
                      <Typography color="error">{formik.errors.passport_photo}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <label>{t("stallform.gst_license")}</label>
                    <input
                      type="file"
                      name="gst_license"
                      className="form-control"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(event) => {
                        formik.setFieldValue("gst_license", event.currentTarget.files);
                      }}
                      style={{ marginBottom: "1rem" }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>

            {/* Terms and Conditions */}
            <StyledCard>
              <h6 style={{ backgroundColor: "#D1E9F6", padding: "10px" }}>
                {t("stallform.terms_conditions")}
              </h6>
              <CardContent>
                <div style={{ backgroundColor: "#f8f9fa", padding: "15px", borderRadius: "5px", marginBottom: "15px" }}>
                  <Typography variant="body2" component="div">
                    <ol>
                      <li>{t("stallform.term_1")}</li>
                      <li>{t("stallform.term_2")}</li>
                      <li>{t("stallform.term_3")}</li>
                      <li>{t("stallform.term_4")}</li>
                      <li>{t("stallform.term_5")}</li>
                    </ol>
                  </Typography>
                </div>
                
                <Typography variant="h6" gutterBottom>
                  {t("stallform.declaration")}
                </Typography>
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isChecked}
                      onChange={handleCheckboxChange}
                      name="termsCheck"
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t("stallform.declaration_text")}
                    </Typography>
                  }
                />
              </CardContent>
            </StyledCard>

            <StyledButton type="submit" variant="contained" color="primary" disabled={!isChecked}>
              {t("stallform.submit")}
            </StyledButton>
          </form>
        </CardContent>
      </StyledCard>
    </div>
  );
};

export default StallApplicationForm;
